import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import { BookOpen, ShoppingCart, Star, Lock, Plus, Minus, Check, Save, AlertTriangle, X, Info } from 'lucide-react';
import { toast } from 'sonner';
import { getBookCover } from '@/lib/assets';

const mandatoryTitles = [
  { id: 1, title: 'The Wonders of Coding', author: 'Sarah Chen', publisher: 'MC Edu', year: 2024, category: 'NF EL', price: 18.50, isbn: '978-981-12345-16', pages: 256, language: 'English', description: 'An engaging introduction to programming concepts for secondary school students, covering computational thinking, algorithms, and basic coding principles.' },
  { id: 2, title: 'Climate Change & You', author: 'David Atkins', publisher: 'Nat. Geo', year: 2025, category: 'NF EL', price: 14.20, isbn: '978-1-4262-0123', pages: 192, language: 'English', description: 'A comprehensive guide to understanding climate change, its causes, effects, and what young people can do to make a difference.' },
  { id: 3, title: 'Singapore Stories Vol 3', author: 'Various', publisher: 'NLB', year: 2025, category: 'NF EL', price: 22.50, isbn: '978-981-12345-2', pages: 320, language: 'English', description: 'A collection of inspiring stories from Singapore\'s history and contemporary life, celebrating the nation\'s diverse heritage.' },
];

const optionalFiction = [
  { id: 10, title: 'Between Two Worlds', author: 'Amelia Tan', publisher: 'Epigram Books', year: 2025, genre: 'Drama', price: 16.80, rating: 4.2, selected: true, isbn: '978-981-12345-9', pages: 288, language: 'English', description: 'A powerful coming-of-age story about identity, family, and finding your place in a multicultural society.' },
  { id: 11, title: 'The Last Library', author: 'Priya Nair', publisher: 'Penguin', year: 2024, genre: 'Mystery', price: 15.90, rating: 4.5, selected: true, isbn: '978-981-12345-10', pages: 312, language: 'English', description: 'A gripping mystery set in a world where books are becoming extinct, following a young librarian\'s quest to save the last collection.' },
  { id: 12, title: 'Code Red', author: 'Marcus Wu', publisher: 'MPH', year: 2025, genre: 'Sci-Fi', price: 17.20, rating: 4.1, selected: true, isbn: '978-981-12345-11', pages: 276, language: 'English', description: 'A thrilling science fiction adventure about a teenage hacker who discovers a conspiracy that threatens the digital world.' },
  { id: 13, title: 'Daughters of Singapore', author: 'Mei Lin Goh', publisher: 'Epigram', year: 2024, genre: 'Historical', price: 16.50, rating: 4.4, selected: true, isbn: '978-981-12345-12', pages: 344, language: 'English', description: 'An epic historical novel spanning three generations of women who shaped Singapore\'s modern identity.' },
  { id: 14, title: 'Sound of Silence', author: 'Ravi Menon', publisher: 'Times Books', year: 2025, genre: 'Literary', price: 15.60, rating: 4.3, selected: true, isbn: '978-981-12345-13', pages: 248, language: 'English', description: 'A contemplative literary fiction exploring themes of isolation, connection, and the power of music.' },
  { id: 15, title: 'Island Voices', author: 'Various', publisher: 'Ethos Books', year: 2025, genre: 'Anthology', price: 18.00, rating: 4.0, selected: true, isbn: '978-981-12345-14', pages: 280, language: 'English', description: 'An anthology of short stories from Southeast Asian writers, showcasing diverse perspectives and storytelling traditions.' },
  { id: 16, title: 'The Memory Keeper', author: 'Siti Rahimah', publisher: 'Penguin', year: 2024, genre: 'Fantasy', price: 16.80, rating: 4.6, selected: true, isbn: '978-981-12345-15', pages: 368, language: 'English', description: 'A magical fantasy novel about a girl who can see and preserve memories, set in a world where forgetting is a choice.' },
  { id: 17, title: 'Across the Sea', author: 'James Tan', publisher: 'MPH', year: 2025, genre: 'Adventure', price: 15.90, rating: 4.2, selected: false, isbn: '978-981-12345-17', pages: 292, language: 'English', description: 'An exciting adventure novel following a group of teens on a journey across the Pacific, discovering friendship and courage.' },
  { id: 18, title: 'The Rain Collector', author: 'Diana Yap', publisher: 'Epigram', year: 2024, genre: 'Literary', price: 14.90, rating: 4.1, selected: false, isbn: '978-981-12345-18', pages: 224, language: 'English', description: 'A lyrical novel about water scarcity, hope, and the resilience of a small village community.' },
];

export default function SchoolTitleSelection() {
  const [selectedTitles, setSelectedTitles] = useState(optionalFiction.filter(t => t.selected).map(t => t.id));
  const [quantities, setQuantities] = useState({});
  const [selectedBook, setSelectedBook] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const totalBudget = 3000;
  const mandatoryTotal = 504 + 528.20;
  const optionalTotal = selectedTitles.reduce((sum, id) => {
    const title = optionalFiction.find(t => t.id === id);
    const qty = quantities[id] || 1;
    return sum + (title?.price || 0) * qty;
  }, 0);
  const budgetUsed = mandatoryTotal + optionalTotal + 1632.70;
  const budgetRemaining = totalBudget - budgetUsed;

  const toggleTitle = (id) => {
    if (selectedTitles.includes(id)) {
      setSelectedTitles(selectedTitles.filter(t => t !== id));
      const newQty = { ...quantities };
      delete newQty[id];
      setQuantities(newQty);
    } else {
      const title = optionalFiction.find(t => t.id === id);
      if (budgetRemaining < title?.price) {
        toast.warning('Budget warning: Adding this title will exceed your budget');
      }
      setSelectedTitles([...selectedTitles, id]);
      setQuantities({ ...quantities, [id]: 1 });
    }
  };

  const updateQuantity = (id, qty) => {
    setQuantities({ ...quantities, [id]: parseInt(qty) });
  };

  const openBookDetail = (book, e) => {
    e.stopPropagation();
    setSelectedBook(book);
    setDetailModalOpen(true);
  };

  const handlePlaceOrder = () => {
    toast.success('Order placed successfully! PO-RGS-CDS-C1-2028 confirmed.');
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
            <BookOpen className="w-7 h-7 text-[#1E4D35]" />
            Browse & Select — CDS Cycle 1 2028
          </h1>
          <p className="text-slate-500 mt-1">Primary Level | Select your optional fiction titles</p>
        </div>
        <Badge className="bg-red-100 text-red-700 px-4 py-2">
          <AlertTriangle className="w-4 h-4 mr-2" />
          Selection closes in 5 days — 15 Sep 2026
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Book Catalogue - 70% */}
        <div className="lg:col-span-2 space-y-6">
          {/* Filter Bar */}
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" size="sm" className="bg-slate-100">All</Button>
            <Button variant="outline" size="sm">📗 Mandatory Included</Button>
            <Button variant="outline" size="sm">📘 Optional Fiction EL</Button>
            <Button variant="outline" size="sm">➕ Add-On Quantities</Button>
            <div className="ml-auto flex items-center gap-2">
              <Select defaultValue="all">
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Genre" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Genres</SelectItem>
                  <SelectItem value="drama">Drama</SelectItem>
                  <SelectItem value="mystery">Mystery</SelectItem>
                  <SelectItem value="scifi">Sci-Fi</SelectItem>
                </SelectContent>
              </Select>
              <Input placeholder="Search..." className="w-40" />
            </div>
          </div>

          {/* Mandatory Titles Section */}
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Lock className="w-4 h-4 text-slate-500" />
                Mandatory Titles — 78 titles automatically included for Primary level
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3">
                {mandatoryTitles.map(title => (
                  <div key={title.id} className="p-3 border rounded-lg bg-slate-50 opacity-80">
                    <img 
                      src={getBookCover(title.title)} 
                      alt={title.title}
                      className="w-full h-28 object-cover rounded mb-2"
                    />
                    <Badge variant="outline" className="mb-1 text-[10px]">
                      <Lock className="w-3 h-3 mr-1" /> Mandatory
                    </Badge>
                    <h4 
                      className="font-medium text-sm line-clamp-1 cursor-pointer hover:text-[#1E4D35] hover:underline"
                      onClick={(e) => openBookDetail(title, e)}
                    >
                      {title.title}
                    </h4>
                    <p className="text-xs text-slate-500">{title.author}</p>
                    <p className="text-sm font-semibold mt-1">${title.price.toFixed(2)}</p>
                    <Badge className="mt-2 bg-slate-200 text-slate-600 text-[10px]">
                      <Lock className="w-3 h-3 mr-1" /> Included
                    </Badge>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-3">+ 75 more mandatory titles (30 NF EL + 48 MTL)</p>
            </CardContent>
          </Card>

          {/* Optional Fiction Section */}
          <Card>
            <CardHeader className="py-4">
              <CardTitle className="font-heading text-base">
                Optional Fiction EL — Select from 60 titles
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                {optionalFiction.map(title => {
                  const isSelected = selectedTitles.includes(title.id);
                  const qty = quantities[title.id] || 1;
                  return (
                    <div 
                      key={title.id} 
                      className={`p-3 border rounded-lg transition-all ${
                        isSelected ? 'border-[#1E4D35] border-2 bg-emerald-50/30' : 'hover:border-slate-300'
                      }`}
                      data-testid={`book-card-${title.id}`}
                    >
                      <div className="w-full h-28 rounded mb-2 relative overflow-hidden">
                        <img 
                          src={getBookCover(title.title)} 
                          alt={title.title}
                          className="w-full h-full object-cover cursor-pointer"
                          onClick={(e) => openBookDetail(title, e)}
                        />
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-[#1E4D35] rounded-full flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 mb-1">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`w-3 h-3 ${i < Math.floor(title.rating) ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
                        ))}
                        <span className="text-xs text-slate-500 ml-1">{title.rating}</span>
                      </div>
                      <h4 
                        className="font-medium text-sm line-clamp-1 cursor-pointer hover:text-[#1E4D35] hover:underline"
                        onClick={(e) => openBookDetail(title, e)}
                      >
                        {title.title}
                      </h4>
                      <p className="text-xs text-slate-500">{title.author} | {title.year}</p>
                      <Badge variant="outline" className="mt-1 text-[10px]">{title.genre}</Badge>
                      <p className="text-sm font-semibold mt-2">${title.price.toFixed(2)}</p>
                      
                      {/* Quantity Dropdown */}
                      {isSelected && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-xs text-slate-500">Qty:</span>
                          <Select 
                            value={String(qty)} 
                            onValueChange={(val) => updateQuantity(title.id, val)}
                          >
                            <SelectTrigger className="h-7 w-16 text-xs" data-testid={`qty-select-${title.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[1, 2, 3, 4, 5].map(n => (
                                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <span className="text-xs text-slate-500">= ${(title.price * qty).toFixed(2)}</span>
                        </div>
                      )}

                      <Button 
                        size="sm" 
                        className={`w-full mt-2 ${isSelected ? 'bg-[#1E4D35]' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                        variant={isSelected ? 'default' : 'outline'}
                        onClick={() => toggleTitle(title.id)}
                      >
                        {isSelected ? (
                          <><Check className="w-3 h-3 mr-1" /> In Your Order</>
                        ) : (
                          <><Plus className="w-3 h-3 mr-1" /> Add to Order</>
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500 mt-3 text-center">Showing 9 of 60 titles</p>
            </CardContent>
          </Card>
        </div>

        {/* Order Summary Sidebar - 30% */}
        <div className="lg:col-span-1">
          <Card className="sticky top-24">
            <CardHeader className="py-4 bg-[#1E4D35] text-white rounded-t-lg">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                Order Summary
              </CardTitle>
              <p className="text-sm text-white/80">CDS Cycle 1 2028 — Orchid Primary</p>
            </CardHeader>
            <CardContent className="pt-4">
              {/* Budget Tracker */}
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-1">
                  <span>Budget Used</span>
                  <span className="font-semibold">${budgetUsed.toFixed(2)}</span>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[#1E4D35] transition-all" 
                    style={{ width: `${Math.min((budgetUsed / totalBudget) * 100, 100)}%` }} 
                  />
                </div>
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>${totalBudget.toLocaleString()} total</span>
                  <span className={budgetRemaining < 50 ? 'text-amber-600 font-medium' : ''}>
                    ${budgetRemaining.toFixed(2)} remaining
                  </span>
                </div>
              </div>

              <ScrollArea className="h-[400px] pr-2">
                {/* Mandatory */}
                <div className="border-b pb-3 mb-3">
                  <p className="text-xs text-slate-500 uppercase font-medium mb-2">Mandatory (auto-included)</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Non-Fiction EL — 30 titles</span>
                      <span>$504.00</span>
                    </div>
                    <div className="flex justify-between">
                      <span>MTL Chinese — 30 titles</span>
                      <span>$297.00</span>
                    </div>
                    <div className="flex justify-between">
                      <span>MTL Malay — 13 titles</span>
                      <span>$161.20</span>
                    </div>
                    <div className="flex justify-between">
                      <span>MTL Tamil — 5 titles</span>
                      <span>$70.00</span>
                    </div>
                  </div>
                </div>

                {/* Optional Fiction */}
                <div className="border-b pb-3 mb-3">
                  <p className="text-xs text-slate-500 uppercase font-medium mb-2">Optional Fiction EL ({selectedTitles.length} selected)</p>
                  <div className="space-y-1 text-sm">
                    {selectedTitles.map(id => {
                      const title = optionalFiction.find(t => t.id === id);
                      const qty = quantities[id] || 1;
                      return (
                        <div key={id} className="flex justify-between">
                          <span className="line-clamp-1">{title?.title} {qty > 1 && `×${qty}`}</span>
                          <span>${((title?.price || 0) * qty).toFixed(2)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </ScrollArea>

              {/* Totals */}
              <div className="border-t pt-3 mt-3">
                <div className="flex justify-between text-sm mb-1">
                  <span>Total Books</span>
                  <span className="font-semibold">{78 + selectedTitles.reduce((sum, id) => sum + (quantities[id] || 1), 0)} copies</span>
                </div>
                <div className="flex justify-between text-lg font-bold">
                  <span>Order Total</span>
                  <span>${budgetUsed.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-slate-500">
                  <span>Budget remaining</span>
                  <span>${budgetRemaining.toFixed(2)}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2 mt-4">
                <Button variant="outline" className="w-full">
                  <Save className="w-4 h-4 mr-2" />
                  Save Draft
                </Button>
                <Button 
                  className="w-full bg-[#1E4D35] hover:bg-[#16402B]"
                  onClick={handlePlaceOrder}
                  data-testid="place-order-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Place Order
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Book Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-2xl">
          {selectedBook && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">{selectedBook.title}</DialogTitle>
                <DialogDescription>by {selectedBook.author}</DialogDescription>
              </DialogHeader>
              
              <div className="grid grid-cols-3 gap-6 mt-4">
                {/* Book Cover */}
                <div>
                  <img 
                    src={getBookCover(selectedBook.title)} 
                    alt={selectedBook.title}
                    className="w-full rounded-lg shadow-lg"
                  />
                  {selectedBook.rating && (
                    <div className="flex items-center gap-1 mt-3 justify-center">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-4 h-4 ${i < Math.floor(selectedBook.rating) ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
                      ))}
                      <span className="text-sm text-slate-600 ml-1">{selectedBook.rating}</span>
                    </div>
                  )}
                </div>

                {/* Book Details */}
                <div className="col-span-2 space-y-4">
                  <p className="text-slate-600">{selectedBook.description}</p>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">ISBN:</span>
                      <span className="ml-2 font-mono">{selectedBook.isbn}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Publisher:</span>
                      <span className="ml-2">{selectedBook.publisher}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Year:</span>
                      <span className="ml-2">{selectedBook.year}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Pages:</span>
                      <span className="ml-2">{selectedBook.pages}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Language:</span>
                      <span className="ml-2">{selectedBook.language}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Category:</span>
                      <span className="ml-2">{selectedBook.category || selectedBook.genre}</span>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-2xl font-bold text-[#1E4D35]">${selectedBook.price.toFixed(2)}</span>
                        <span className="text-sm text-slate-500 ml-2">per copy</span>
                      </div>
                      {!selectedBook.category && (
                        <Button 
                          className="bg-[#1E4D35] hover:bg-[#16402B]"
                          onClick={() => {
                            if (!selectedTitles.includes(selectedBook.id)) {
                              toggleTitle(selectedBook.id);
                            }
                            setDetailModalOpen(false);
                            toast.success(`"${selectedBook.title}" added to order`);
                          }}
                          data-testid="add-from-modal-btn"
                        >
                          {selectedTitles.includes(selectedBook.id) ? (
                            <><Check className="w-4 h-4 mr-2" /> In Your Order</>
                          ) : (
                            <><Plus className="w-4 h-4 mr-2" /> Add to Order</>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
